import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useId } from 'react'
import type { BackupRulesConfig, PreviousBackupInfo } from '@companion-app/shared/Model/UserConfigModel.js'
import { StaticAlert } from '~/Components/Alert.js'
import { Button } from '~/Components/Button'
import { SimpleDropdownInputField } from '~/Components/DropdownInputFieldSimple.js'
import { Form, FormLabel, InputGroup } from '~/Components/Form.js'
import { Grid } from '~/Components/Grid'
import { trpc, useMutationExt } from '~/Resources/TRPC.js'
import { NumberInputField } from '../Components/NumberInputField.js'
import { TextInputField, TextInputFieldSimple } from '../Components/TextInputField.js'
import { RootAppStoreContext } from '../Stores/RootAppStore.js'
import { backupTypes } from './BackupConstants.js'

interface PreviousBackupRowProps {
	backup: PreviousBackupInfo
	ruleId: string
}

const PreviousBackupRow = observer(function PreviousBackupRow({ backup, ruleId }: PreviousBackupRowProps) {
	const { notifier } = useContext(RootAppStoreContext)

	const deleteBackupFileMutation = useMutationExt(trpc.importExport.backupRules.deleteBackupFile.mutationOptions())

	const deleteBackup = useCallback(() => {
		if (confirm('Are you sure you want to delete this backup file?')) {
			deleteBackupFileMutation.mutateAsync({ ruleId, filePath: backup.filePath }).catch((err) => {
				console.error('Error deleting backup:', err)
				notifier.show('Error', `Failed to delete backup file: ${err.message || err}`, 5000)
			})
		}
	}, [deleteBackupFileMutation, notifier, ruleId, backup.filePath])

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
	}

	const getFileName = (filePath: string): string => {
		return filePath.split('/').pop() || filePath
	}

	return (
		<tr>
			<td>
				<span
					title={getFileName(backup.filePath)}
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						fontWeight: 500,
					}}
				>
					{getFileName(backup.filePath)}
				</span>
				<br />
				<small>
					{new Date(backup.createdAt).toLocaleString()} • {formatFileSize(backup.fileSize)}
				</small>
			</td>
			<td className="no-wrap" style={{ verticalAlign: 'middle' }}>
				<Button color="danger" size="sm" onClick={deleteBackup} title="Delete backup">
					<FontAwesomeIcon icon={faTrash} />
				</Button>
			</td>
		</tr>
	)
})

interface BackupRuleEditorProps {
	ruleId: string
}

export const BackupRuleEditor = observer(function BackupRuleEditor({ ruleId }: BackupRuleEditorProps) {
	const { userConfig, notifier } = useContext(RootAppStoreContext)

	const updateRuleFieldMutation = useMutationExt(trpc.importExport.backupRules.updateRuleField.mutationOptions())
	const runBackupNowMutation = useMutationExt(trpc.importExport.backupRules.runBackupNow.mutationOptions())

	// Find the rule in the config
	const rule = userConfig.properties?.backups?.find((r) => r.id === ruleId)

	// Function to update a specific field in the rule
	const updateField = useCallback(
		<K extends keyof BackupRulesConfig>(field: K, value: BackupRulesConfig[K]) => {
			updateRuleFieldMutation.mutateAsync({ ruleId, field, value }).catch((err) => {
				console.error('Error updating backup rule field:', err)
			})
		},
		[updateRuleFieldMutation, ruleId]
	)

	// Function to run the backup rule immediately
	const runNow = useCallback(() => {
		runBackupNowMutation
			.mutateAsync({ ruleId })
			.then(() => {
				notifier.show('Success', 'Backup created successfully', 3000)
			})
			.catch((err) => {
				console.error('Error running backup now:', err)
				notifier.show('Error', `${err.message || err || 'Failed to create backup'}`, 5000)
			})
	}, [runBackupNowMutation, notifier, ruleId])

	const nameFieldId = useId()
	const cronFieldId = useId()
	const backupTypeFieldId = useId()
	const backupPathFieldId = useId()
	const backupNamePatternFieldId = useId()
	const keepFieldId = useId()

	// If no rule found, show message
	if (!rule) {
		return <StaticAlert color="warning">Backup rule not found</StaticAlert>
	}

	const previousBackups = [...(rule.previousBackups || [])].sort((a, b) => b.createdAt - a.createdAt)

	return (
		<Form className="p-3 row g-sm-2">
			<FormLabel htmlFor={nameFieldId} className="col-sm-4 col-form-label col-form-label-sm">
				Rule Name
			</FormLabel>
			<Grid.Col className={`fieldtype-textinput`} sm={8}>
				<InputGroup>
					<TextInputFieldSimple id={nameFieldId} value={rule.name} setValue={(value) => updateField('name', value)} />
					<Button color="warning" onClick={runNow}>
						Run Now
					</Button>
				</InputGroup>
			</Grid.Col>

			<FormLabel htmlFor={cronFieldId} className="col-sm-4 col-form-label col-form-label-sm">
				Cron Schedule
			</FormLabel>
			<Grid.Col className={`fieldtype-textinput`} sm={8}>
				<TextInputFieldSimple id={cronFieldId} value={rule.cron} setValue={(value) => updateField('cron', value)} />
			</Grid.Col>
			<Grid.Col className={`fieldtype-textinput mt-0`} sm={{ offset: 4, span: 8 }}>
				<small className="form-text text-muted">
					Use cron syntax (e.g., "0 0 * * *" for daily at midnight). You can use{' '}
					<a href="https://crontab.guru" target="_blank" rel="noopener noreferrer">
						crontab guru
					</a>{' '}
					to help you generate the correct syntax.
				</small>
			</Grid.Col>

			<FormLabel htmlFor={backupTypeFieldId} className="col-sm-4 col-form-label col-form-label-sm">
				Backup Type
			</FormLabel>
			<Grid.Col className={`fieldtype-textinput`} sm={8}>
				<SimpleDropdownInputField
					id={backupTypeFieldId}
					value={rule.backupType}
					setValue={(value) => updateField('backupType', value as BackupRulesConfig['backupType'])}
					choices={backupTypes}
				/>
			</Grid.Col>
			{rule.backupType === 'db' && (
				<Grid.Col sm={12}>
					<StaticAlert color="warning" className="mt-2">
						Raw backups are a direct copy of the database file. They cannot be restored through the web interface, but
						contain more data than the default exports.
					</StaticAlert>
				</Grid.Col>
			)}

			<FormLabel htmlFor={backupPathFieldId} className="col-sm-4 col-form-label col-form-label-sm">
				Backup Path
			</FormLabel>
			<Grid.Col className={`fieldtype-textinput`} sm={8}>
				<TextInputFieldSimple
					id={backupPathFieldId}
					value={rule.backupPath}
					setValue={(value) => updateField('backupPath', value)}
				/>
			</Grid.Col>
			<Grid.Col className={`fieldtype-textinput mt-0`} sm={{ offset: 4, span: 8 }}>
				<small className="form-text text-muted">
					Directory path where backups will be saved. Leave empty for default location.
				</small>
			</Grid.Col>

			<FormLabel htmlFor={backupNamePatternFieldId} className="col-sm-4 col-form-label col-form-label-sm">
				Backup Name Pattern
			</FormLabel>
			<Grid.Col className={`fieldtype-textinput`} sm={8}>
				<TextInputField
					id={backupNamePatternFieldId}
					value={rule.backupNamePattern}
					setValue={(value) => updateField('backupNamePattern', value)}
					useVariables
				/>
			</Grid.Col>

			<FormLabel htmlFor={keepFieldId} className="col-sm-4 col-form-label col-form-label-sm">
				Number of Backups to Keep
			</FormLabel>
			<Grid.Col className={`fieldtype-textinput`} sm={8}>
				<NumberInputField id={keepFieldId} value={rule.keep} min={1} setValue={(value) => updateField('keep', value)} />
			</Grid.Col>
			<Grid.Col className={`fieldtype-textinput mt-0`} sm={{ offset: 4, span: 8 }}>
				<small className="form-text text-muted">How many backup files to retain before deleting the oldest ones</small>
			</Grid.Col>

			<Grid.Col sm={12}>
				<label className="form-label">Previous Backups</label>
				{rule.previousBackups && rule.previousBackups.length > 0 && (
					<div className="table-responsive">
						<table className="table table-sm table-striped">
							<tbody>
								{previousBackups.map((backup) => (
									<PreviousBackupRow key={`${backup.filePath}-${backup.createdAt}`} backup={backup} ruleId={ruleId} />
								))}
							</tbody>
						</table>
					</div>
				)}

				{(!rule.previousBackups || rule.previousBackups.length === 0) && (
					<div className="text-muted">
						<small>No backup files found. Backups may have been manually deleted or moved.</small>
					</div>
				)}
			</Grid.Col>
		</Form>
	)
})
